import { Injectable } from '@angular/core';
import {
    UserProfile,
    UserProfileService,
} from '@polaris-customerweb/sharedcomponents.user-profile';
import { Observable, exhaustMap, map, of, tap } from 'rxjs';
import { UserAuthService } from '../state/user-auth/user-auth.service';
import { Eula, EulaContentContract } from '../types/content-types';
import { ContentfulArrayContract } from '../types/content-types/contracts';
import { ContentResourceService } from './content-resource.service';

@Injectable({ providedIn: 'root' })
export class EulaService {
    private isEulaAccepted: boolean;

    constructor(
        private contentResourceService: ContentResourceService,
        private userAuthService: UserAuthService,
        private userProfileService: UserProfileService
    ) {}

    getEula(): Observable<Eula> {
        return this.contentResourceService
            .getEntries<
                ContentfulArrayContract<EulaContentContract>
            >({ content_type: 'eula' })
            .pipe(map((entries) => new Eula(entries.items[0])));
    }

    acceptCurrentEula(): Observable<UserProfile | null> {
        return this.getCurrentEulaVersion().pipe(
            tap(() => (this.isEulaAccepted = true)),
            exhaustMap((eulaVersion) =>
                this.userProfileService.acceptEula$(eulaVersion)
            )
        );
    }

    declineCurrentEula(): void {
        this.isEulaAccepted = false;
    }

    isEulaConfirmCurrent(): Observable<boolean> {
        if (this.isEulaAccepted !== undefined) {
            return of(this.isEulaAccepted);
        }

        return this.userAuthService.isAuthenticated$.pipe(
            exhaustMap((isAuthenticated) => {
                if (!isAuthenticated) {
                    return of(true);
                }

                return this.getCurrentEulaVersion().pipe(
                    exhaustMap((eulaVersion) => {
                        if (!eulaVersion) {
                            return of(true);
                        }

                        return this.userProfileService.isEulaAccepted$(
                            eulaVersion
                        );
                    })
                );
            }),
            tap((isAccepted) => (this.isEulaAccepted = isAccepted))
        );
    }

    private getCurrentEulaVersion(): Observable<string> {
        return this.getEula().pipe(map((eula) => eula?.version ?? ''));
    }
}
